// server/browser/src/lib/api.ts
//
// Thin REST client for the cloud. Every call carries the single PKCE
// access token from auth.ts; a 401 drops the token and raises so callers
// can redirect to sign-in. All endpoints live under /v1/* — the billing
// surface here, /v1/admin/* in lib/admin.ts, /v1/remote/* in the remote
// section.

import { getAccessToken, signOut } from "./auth";
import { platformOrNull } from "./remote/platform";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

/** Shared request primitive reused by every /v1/* client. */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = { accept: "application/json" };
  if (token) headers["authorization"] = `Bearer ${token}`;
  if (opts.body !== undefined) headers["content-type"] = "application/json";

  // Prepend the cloud base so native shells (iOS, whose WebView origin is
  // local) hit the cloud, not the device. On browser site/admin there's no
  // adapter → base "" → same-origin relative path as before.
  let url = (platformOrNull()?.getCloudBaseUrl() ?? "") + path;
  if (opts.query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== "") qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  const res = await fetch(url, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401) {
    signOut();
    throw new ApiError(401, "Unauthorized");
  }
  if (res.status === 503) {
    throw new ApiError(503, "BILLING_DISABLED");
  }
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* empty/non-JSON body */
  }
  if (!res.ok) {
    const detail =
      (payload as { detail?: string } | null)?.detail || `HTTP ${res.status}`;
    throw new ApiError(res.status, detail);
  }
  return payload as T;
}

// ---- response shapes ---------------------------------------------------

export interface Entitlements {
  user_id: string;
  plan: "basic" | "pro" | "max";
  source: "free" | "paid" | "promo" | "manual" | "group";
  /** When the entitlement *stops* (e.g. promo expiry, cancelled sub).
   *  null for active recurring subs — see ``renews_at`` for the next bill. */
  valid_until: string | null;
  /** Next billing date for paid subs. null for free/promo/manual. */
  renews_at: string | null;
  /** When true, the paid sub will lapse at ``renews_at`` and NOT auto-renew. */
  cancel_at_period_end: boolean;
  /** Pending scheduled action — set by Paddle when the user (or admin)
   *  schedules a change that takes effect at the next billing period.
   *  One of: "cancel" | "change_plan" | "pause" | "resume" | null. */
  scheduled_change_action: string | null;
  /** When the scheduled action takes effect. null when no change pending. */
  scheduled_change_effective_at: string | null;
  features: {
    remote_control: boolean;
    memory_rag: boolean;
    advanced_tools: boolean;
    workflows: boolean;
    priority_models: boolean;
  };
  limits: { ai_credits_monthly: number; max_devices: number };
  usage: { ai_credits_used: number };
}

export interface CheckoutResponse {
  transaction_id: string;
  client_token: string;
}

export interface RedeemResponse {
  success: boolean;
  plan?: string;
  valid_until?: string;
  error?: string;
}

/** One row per (plan, interval) the cloud has a Paddle price configured for. */
export interface PricingPlan {
  plan: "pro" | "max";
  interval: "monthly" | "yearly";
  price_id: string; // empty when is_placeholder
  amount_minor: number;
  amount_decimal: string; // "19.00"
  currency: string; // "USD"
  billing_cycle_interval: string | null; // "month" | "year"
  billing_cycle_frequency: number | null;
  /** Monthly token allowance for this plan, from server-side caps. Rendered
   *  on the card so the number can't drift from plan_catalog. */
  monthly_credits?: number;
  name: string;
  /** True when the cloud filled this slot with a demo number because
   *  Paddle wasn't fully configured (no API key, missing PRICE_ID, fetch
   *  failed, …). Surface visually and disable Upgrade. */
  is_placeholder?: boolean;
}

export interface PricingResponse {
  billing_enabled: boolean;
  currency: string;
  plans: PricingPlan[];
  errors: string[];
  /** True when *any* row is a placeholder — the page is in demo mode. */
  is_placeholder?: boolean;
  /** True when the four PADDLE_*_PRICE_ID slots are populated AND billing
   *  is enabled. The Upgrade button keys off THIS, not is_placeholder —
   *  display readiness and checkout readiness are independent (e.g. the
   *  API key may lack prices:read but still hold transactions:write). */
  checkout_configured?: boolean;
  /** Per-plan monthly token allowance from server-side caps, keyed by plan id
   *  ("basic" | "pro" | "max"). The marketing page renders these so the
   *  advertised number can't drift from plan_catalog. */
  monthly_credits_by_plan?: Record<string, number>;
}

// ---- endpoints ---------------------------------------------------------

export const api = {
  me: () => request<Record<string, unknown>>("/v1/me"),
  entitlements: () => request<Entitlements>("/v1/me/entitlements"),
  subscription: () => request<Record<string, unknown>>("/v1/me/subscription"),
  usage: () => request<Record<string, unknown>>("/v1/me/usage"),
  // Public, no auth needed — the marketing pricing card calls this
  // before sign-in. ApiError(401) won't fire because we don't send a
  // bearer when getAccessToken() is empty.
  pricing: () => request<PricingResponse>("/v1/pricing"),
  checkout: (plan: string, interval: string) =>
    request<CheckoutResponse>("/v1/billing/checkout", {
      method: "POST",
      body: { plan, interval },
    }),
  portal: () => request<{ portal_url: string }>("/v1/billing/portal", { method: "POST" }),
  redeem: (code: string) =>
    request<RedeemResponse>("/v1/promo/redeem", { method: "POST", body: { code } }),
};
